import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import PageTitle from '../components/PageTitle';
import api from '../api/api';
import '../styles/pages/SeasoningsSettingsPage.css';
import '../styles/components/AllergiesSection.css';

/**
 * SeasoningsSettingsPage - 調味料設定ページ
 * ユーザーが持っている調味料を登録して、レシピ生成時に活用
 */
const SeasoningsSettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // プリセット選択
  const [selectedPreset, setSelectedPreset] = useState('');

  // カスタム調味料リスト
  const [customSeasonings, setCustomSeasonings] = useState([]);

  // 新規追加用入力
  const [newSeasoning, setNewSeasoning] = useState('');

  // 保存状態
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // 商品データ（カテゴリー別）
  const [recommendedProducts, setRecommendedProducts] = useState({});

  // 食材制限（アレルギー・避けたい食材）
  const [allergies, setAllergies] = useState([]);
  const [allergiesOther, setAllergiesOther] = useState('');
  const [avoidFoods, setAvoidFoods] = useState([]);
  const [avoidFoodsOther, setAvoidFoodsOther] = useState('');

  // プリセット定義（バックエンドと同じ - オーソモレキュラー対応）
  const presets = [
    {
      value: 'minimal',
      name: 'ミニマルセット',
      description: '基本の5種類だけ',
      seasonings: ['醤油', '塩', '砂糖', '油', '味噌'],
      icon: '🥄'
    },
    {
      value: 'full',
      name: 'しっかりセット',
      description: '自然派調味料で幅広く対応',
      seasonings: [
        '醤油', '塩', '砂糖', '油', '味噌',
        '本みりん', '米酢', '純米料理酒',
        'かつお節', '昆布',
        'オリーブオイル', 'ごま油',
        'きび糖', 'てんさい糖',
        '胡椒', '生姜', 'にんにく',
        'ごま', '海苔', '梅干し'
      ],
      icon: '🍳'
    }
  ];

  // カスタム用の選択肢リスト（グループ化）
  const seasoningGroups = [
    {
      name: '基本調味料',
      items: ['醤油', '塩', '砂糖', '油', '味噌']
    },
    {
      name: '発酵調味料',
      items: ['本みりん', '米酢', '純米料理酒']
    },
    {
      name: 'だし',
      items: ['かつお節', '昆布', '煮干し', '干し椎茸']
    },
    {
      name: '油類',
      items: ['オリーブオイル', 'ごま油', '米油', '亜麻仁油']
    },
    {
      name: '糖類',
      items: ['きび糖', 'てんさい糖', 'はちみつ', 'メープルシロップ']
    },
    {
      name: 'スパイス',
      items: ['胡椒', '七味', 'カレー粉', 'シナモン', 'ターメリック']
    },
    {
      name: '生鮮',
      items: ['生姜', 'にんにく', 'ねぎ', 'レモン', 'ゆず']
    },
    {
      name: 'その他',
      items: ['ごま', '海苔', '梅干し', 'ポン酢']
    }
  ];

  // ページタイトル設定
  useEffect(() => {
    document.title = '調味料設定 | つむぎ';
  }, []);

  // 現在の設定を取得
  useEffect(() => {
    fetchCurrentSettings();
    fetchUserProfile();
    fetchRecommendedProducts();
  }, []);

  const fetchCurrentSettings = async () => {
    try {
      const response = await api.get('/api/seasonings');
      if (response.data.success) {
        const { preset, customList } = response.data.seasonings;
        setSelectedPreset(preset || '');
        setCustomSeasonings(customList || []);
      }
    } catch (error) {
      console.error('Failed to fetch seasonings:', error);
    }
  };

  // ユーザープロフィール取得（食材制限情報）
  const fetchUserProfile = async () => {
    try {
      const response = await api.get('/api/profile');
      if (response.data.success) {
        const profile = response.data.profile;
        setAllergies(profile.allergies || []);
        setAllergiesOther(profile.allergiesOther || '');
        setAvoidFoods(profile.avoidFoods || []);
        setAvoidFoodsOther(profile.avoidFoodsOther || '');
      }
    } catch (error) {
      console.error('Failed to fetch profile:', error);
    }
  };

  // おすすめ商品を取得
  const fetchRecommendedProducts = async () => {
    const categories = ['塩', '醤油', '味噌', '本みりん', '砂糖', 'にんにく', '生姜'];
    const products = {};

    for (const category of categories) {
      try {
        const response = await api.get(`/api/affiliate/products/category/${category}`, {
          params: { limit: 1, random: 'true' }
        });
        if (response.data.success && response.data.products.length > 0) {
          products[category] = response.data.products[0];
        }
      } catch (error) {
        console.error(`商品取得エラー (${category}):`, error);
      }
    }

    setRecommendedProducts(products);
  };

  // プリセット選択（自動保存）
  const handlePresetSelect = async (presetValue) => {
    setSelectedPreset(presetValue);
    // プリセット選択時はカスタムリストをクリア
    if (presetValue !== 'custom') {
      setCustomSeasonings([]);
      // 自動保存
      await savePreset(presetValue);
    }
  };

  // プリセット自動保存
  const savePreset = async (presetValue) => {
    setIsSaving(true);
    setSaveSuccess(false);

    try {
      const response = await api.post('/api/seasonings', {
        preset: presetValue,
        customList: []
      });

      if (response.data.success) {
        setSaveSuccess(true);
        // 2秒後に成功メッセージを消す
        setTimeout(() => {
          setSaveSuccess(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to save preset:', error);
      alert('保存に失敗しました。もう一度お試しください。');
    } finally {
      setIsSaving(false);
    }
  };

  // カスタムモードに切り替え
  const handleCustomMode = () => {
    setSelectedPreset('custom');
    // 現在選択されているプリセットの調味料をカスタムリストに追加
    if (selectedPreset && selectedPreset !== 'custom') {
      const preset = presets.find(p => p.value === selectedPreset);
      if (preset) {
        setCustomSeasonings([...preset.seasonings]);
      }
    }
  };

  // カスタム調味料の選択切り替え（チェックボックス）
  const handleToggleSeasoning = (seasoning) => {
    if (customSeasonings.includes(seasoning)) {
      // 削除
      setCustomSeasonings(customSeasonings.filter(s => s !== seasoning));
    } else {
      // 追加
      setCustomSeasonings([...customSeasonings, seasoning]);
    }
  };

  // 保存（カスタム設定用）
  const handleSave = async () => {
    setIsSaving(true);
    setSaveSuccess(false);

    try {
      // 調味料設定を保存
      const seasoningResponse = await api.post('/api/seasonings', {
        preset: selectedPreset,
        customList: selectedPreset === 'custom' ? customSeasonings : []
      });

      // 食材制限を保存
      const profileResponse = await api.put('/api/profile', {
        allergies,
        allergiesOther,
        avoidFoods,
        avoidFoodsOther
      });

      if (seasoningResponse.data.success && profileResponse.data.success) {
        setSaveSuccess(true);
        // 2秒後に成功メッセージを消す
        setTimeout(() => {
          setSaveSuccess(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      alert('保存に失敗しました。もう一度お試しください。');
    } finally {
      setIsSaving(false);
    }
  };

  // リセット
  const handleReset = async () => {
    if (!window.confirm('調味料設定をリセットしますか？')) return;

    try {
      await api.delete('/api/seasonings');
      setSelectedPreset('');
      setCustomSeasonings([]);
      alert('リセットしました');
    } catch (error) {
      console.error('Failed to reset seasonings:', error);
      alert('リセットに失敗しました');
    }
  };

  // 現在の調味料リストを取得
  const getCurrentSeasonings = () => {
    if (selectedPreset === 'custom') {
      return customSeasonings;
    } else if (selectedPreset) {
      const preset = presets.find(p => p.value === selectedPreset);
      return preset ? preset.seasonings : [];
    }
    return [];
  };

  const currentSeasonings = getCurrentSeasonings();

  return (
    <AppLayout>
      <PageTitle
        title="調味料・NG食材設定"
        subtitle="レシピと栄養相談で自動反映されます"
        backPath="/recipe-hub"
        backLabel="レシピ一覧"
      />

      {/* 保存成功メッセージ（トースト） */}
      {saveSuccess && (
        <div className="save-success-toast">
          ✓ 保存しました！
        </div>
      )}

      <div className="seasonings-settings-page">
        {/* 説明とナビゲーション */}
        <div className="tsumugi-card tsumugi-card--honey mb-4">
          <h3 className="tsumugi-card-title">💡 このページについて</h3>
          <p className="tsumugi-card-text mb-3">
            お手持ちの調味料とアレルギーや避けたい食材を登録しておくと、レシピ生成や栄養相談で自動的に考慮されます。
          </p>
          <div className="quick-nav">
            <a href="#seasonings-section" className="quick-nav-link">
              🥄 調味料設定へ
            </a>
            <a href="#food-restrictions-section" className="quick-nav-link">
              🚫 NG食材設定へ
            </a>
          </div>
        </div>

        {/* プリセット選択 */}
        <div id="seasonings-section" className="tsumugi-card mb-4">
          <h3 className="tsumugi-card-title">🥄 調味料設定</h3>
          <p className="text-hint text-sm mb-3">
            プリセットから選ぶ（選択すると自動保存されます）
          </p>

          <div className="preset-grid">
            {presets.map((preset) => (
              <div
                key={preset.value}
                className={`preset-card ${selectedPreset === preset.value ? 'preset-card--selected' : ''}`}
                onClick={() => handlePresetSelect(preset.value)}
              >
                <div className="preset-card__icon">{preset.icon}</div>
                <div className="preset-card__name">{preset.name}</div>
                <div className="preset-card__description">{preset.description}</div>
                <div className="preset-card__count">{preset.seasonings.length}種類</div>

                {/* 調味料リスト表示（全て表示） */}
                <div className="preset-card__seasonings">
                  {preset.seasonings.join('、')}
                </div>

                {selectedPreset === preset.value && (
                  <div className="preset-card__check">✓</div>
                )}
              </div>
            ))}
          </div>

          <button
            className="tsumugi-btn tsumugi-btn-primary w-full mt-3 seasonings-settings__customize-btn"
            onClick={handleCustomMode}
          >
            ✨ 自分でカスタマイズする
          </button>
        </div>

        {/* カスタム調味料設定 */}
        {selectedPreset === 'custom' && (
          <div className="tsumugi-card tsumugi-card--mint mb-4">
            <h3 className="tsumugi-card-title">カスタム調味料設定</h3>
            <p className="text-hint text-sm mb-3">
              持っている調味料にチェックを入れてください（自然派・無添加のものを優先しています）
            </p>

            {/* グループ化されたチェックボックス選択 */}
            {seasoningGroups.map((group, groupIndex) => (
              <div key={groupIndex} className="custom-seasoning-group">
                <h4 className="custom-seasoning-group__title">{group.name}</h4>
                <div className="custom-seasonings-checkbox-grid">
                  {group.items.map((seasoning, index) => (
                    <label key={index} className="custom-seasoning-checkbox">
                      <input
                        type="checkbox"
                        checked={customSeasonings.includes(seasoning)}
                        onChange={() => handleToggleSeasoning(seasoning)}
                      />
                      <span className="custom-seasoning-checkbox__label">{seasoning}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <p className="text-hint text-xs mt-3 seasonings-settings__selection-count">
              選択中: {customSeasonings.length}種類
            </p>

            {/* カスタム調味料の保存ボタン */}
            <div className="action-buttons mt-4">
              <button
                className="tsumugi-btn tsumugi-btn-primary"
                onClick={handleSave}
                disabled={isSaving || customSeasonings.length === 0}
              >
                {isSaving ? '保存中...' : '保存する'}
              </button>

              <button
                className="tsumugi-btn tsumugi-btn-outline"
                onClick={handleReset}
              >
                リセット
              </button>
            </div>
          </div>
        )}

        {/* 現在の調味料一覧 */}
        {currentSeasonings.length > 0 && (
          <div className="tsumugi-card mb-4">
            <h3 className="tsumugi-card-title">登録される調味料（{currentSeasonings.length}種類）</h3>
            <div className="current-seasonings-grid">
              {currentSeasonings.map((seasoning, index) => (
                <div key={index} className="seasoning-tag">
                  {seasoning}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* アップグレードのヒント */}
        <div className="tsumugi-card tsumugi-card--honey mb-4">
          <h3 className="tsumugi-card-title">💡 少しずつ体に優しい調味料に</h3>
          <p className="tsumugi-card-text text-sm mb-3">
            今ある調味料で十分です。ただ、買い替えの機会があれば、こんな選び方ができるといいかもしれません：
          </p>

          <div className="upgrade-tips">
            <div className="upgrade-tip-item">
              <span className="upgrade-priority upgrade-priority--high">優先度高</span>
              <div className="upgrade-tip-content">
                <strong>塩 → 天然塩</strong>
                <p className="text-hint text-xs">精製していないもの。比較的安価でミネラルが摂れます</p>
                {recommendedProducts['塩'] && (
                  <p className="text-xs mt-1">
                    <a
                      href={recommendedProducts['塩'].affiliateLinks?.moshimo?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="product-link"
                    >
                      おすすめはこちら →
                    </a>
                  </p>
                )}
              </div>
            </div>

            <div className="upgrade-tip-item">
              <span className="upgrade-priority upgrade-priority--high">優先度高</span>
              <div className="upgrade-tip-content">
                <strong>醤油 → 無添加・本醸造</strong>
                <p className="text-hint text-xs">毎日使うものなので影響大。原材料がシンプルなものを</p>
                {recommendedProducts['醤油'] && (
                  <p className="text-xs mt-1">
                    <a
                      href={recommendedProducts['醤油'].affiliateLinks?.moshimo?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="product-link"
                    >
                      おすすめはこちら →
                    </a>
                  </p>
                )}
              </div>
            </div>

            <div className="upgrade-tip-item">
              <span className="upgrade-priority upgrade-priority--high">優先度高</span>
              <div className="upgrade-tip-content">
                <strong>味噌 → 無添加・本醸造</strong>
                <p className="text-hint text-xs">毎日使うものなので影響大。原材料がシンプルなものを</p>
                {recommendedProducts['味噌'] && (
                  <p className="text-xs mt-1">
                    <a
                      href={recommendedProducts['味噌'].affiliateLinks?.moshimo?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="product-link"
                    >
                      おすすめはこちら →
                    </a>
                  </p>
                )}
              </div>
            </div>

            <div className="upgrade-tip-item">
              <span className="upgrade-priority upgrade-priority--medium">できれば</span>
              <div className="upgrade-tip-content">
                <strong>みりん → 本みりん</strong>
                <p className="text-hint text-xs">「みりん風調味料」ではなく、アルコール分が含まれているものが本物</p>
                {recommendedProducts['本みりん'] && (
                  <p className="text-xs mt-1">
                    <a
                      href={recommendedProducts['本みりん'].affiliateLinks?.moshimo?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="product-link"
                    >
                      おすすめはこちら →
                    </a>
                  </p>
                )}
              </div>
            </div>

            <div className="upgrade-tip-item">
              <span className="upgrade-priority upgrade-priority--medium">できれば</span>
              <div className="upgrade-tip-content">
                <strong>砂糖 → きび糖・てんさい糖</strong>
                <p className="text-hint text-xs">精製度が低く、ミネラルが残っています</p>
                {recommendedProducts['砂糖'] && (
                  <p className="text-xs mt-1">
                    <a
                      href={recommendedProducts['砂糖'].affiliateLinks?.moshimo?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="product-link"
                    >
                      おすすめはこちら →
                    </a>
                  </p>
                )}
              </div>
            </div>

            <div className="upgrade-tip-item">
              <span className="upgrade-priority upgrade-priority--low">余裕があれば</span>
              <div className="upgrade-tip-content">
                <strong>チューブ → 生のにんにく</strong>
                <p className="text-hint text-xs">チューブは添加物が多め。生のにんにくをその都度むいて使うのが理想です</p>
                {recommendedProducts['にんにく'] && (
                  <p className="text-xs mt-1">
                    <a
                      href={recommendedProducts['にんにく'].affiliateLinks?.moshimo?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="product-link"
                    >
                      おすすめはこちら →
                    </a>
                  </p>
                )}
              </div>
            </div>

            <div className="upgrade-tip-item">
              <span className="upgrade-priority upgrade-priority--low">余裕があれば</span>
              <div className="upgrade-tip-content">
                <strong>チューブ → 生の生姜</strong>
                <p className="text-hint text-xs">チューブは添加物が多め。生の生姜をその都度すりおろすのが理想です</p>
                {recommendedProducts['生姜'] && (
                  <p className="text-xs mt-1">
                    <a
                      href={recommendedProducts['生姜'].affiliateLinks?.moshimo?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="product-link"
                    >
                      おすすめはこちら →
                    </a>
                  </p>
                )}
              </div>
            </div>
          </div>

          <p className="text-hint text-xs mt-3">
            ※ 一度に全部変える必要はありません。無理のない範囲で、少しずつで大丈夫です
          </p>
        </div>

        {/* 食材制限セクション */}
        <div id="food-restrictions-section" className="tsumugi-card tsumugi-card--peach mb-4">
          <h3 className="tsumugi-card-title">🚫 NG食材・アレルギー設定</h3>
          <p className="text-hint text-sm mb-3">
            アレルギーや避けたい食材を設定すると、レシピ生成と栄養相談で自動的に除外されます
          </p>

          {/* アレルギー食材 */}
          <div className="allergies-section__category">
            <h4 className="allergies-section__category-title">🚨 アレルギー食材</h4>

            <div className="allergies-section__checkbox-grid">
              {['卵', '乳（牛乳）', '小麦', 'えび', 'かに', '落花生（ピーナッツ）', 'そば', '魚類'].map((allergy) => {
                const isChecked = allergies.includes(allergy);
                return (
                  <label
                    key={allergy}
                    className={`allergies-section__checkbox-card ${isChecked ? 'allergies-section__checkbox-card--selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {
                        if (isChecked) {
                          setAllergies(allergies.filter(a => a !== allergy));
                        } else {
                          setAllergies([...allergies, allergy]);
                        }
                      }}
                      className="allergies-section__checkbox"
                    />
                    <span className="allergies-section__checkbox-label">
                      {isChecked && <span className="allergies-section__check-icon">✓</span>}
                      {allergy}
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="allergies-section__input-field">
              <label className="allergies-section__label">その他のアレルギー食材</label>
              <input
                type="text"
                className="allergies-section__input"
                value={allergiesOther}
                onChange={(e) => setAllergiesOther(e.target.value)}
                placeholder="例: バナナ、キウイ"
              />
            </div>
          </div>

          {/* 避けたい食材 */}
          <div className="allergies-section__category">
            <h4 className="allergies-section__category-title">❌ 避けたい食材</h4>
            <p className="allergies-section__category-hint">
              アレルギーではないが避けたい食材（嫌いな食べ物、宗教的制限など）
            </p>

            <div className="allergies-section__checkbox-grid">
              {['肉類全般', '魚類全般', '乳製品全般', '卵全般', '動物性食品全般（ヴィーガン）', '豚肉（ハラール）', 'グルテン', '化学調味料全般', '人工甘味料'].map((food) => {
                const isChecked = avoidFoods.includes(food);
                return (
                  <label
                    key={food}
                    className={`allergies-section__checkbox-card ${isChecked ? 'allergies-section__checkbox-card--selected' : ''}`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => {
                        if (isChecked) {
                          setAvoidFoods(avoidFoods.filter(f => f !== food));
                        } else {
                          setAvoidFoods([...avoidFoods, food]);
                        }
                      }}
                      className="allergies-section__checkbox"
                    />
                    <span className="allergies-section__checkbox-label">
                      {isChecked && <span className="allergies-section__check-icon">✓</span>}
                      {food}
                    </span>
                  </label>
                );
              })}
            </div>

            <div className="allergies-section__input-field">
              <label className="allergies-section__label">その他の避けたい食材</label>
              <input
                type="text"
                className="allergies-section__input"
                value={avoidFoodsOther}
                onChange={(e) => setAvoidFoodsOther(e.target.value)}
                placeholder="例: レバー、チーズ、パクチー"
              />
            </div>
          </div>

          {/* 食材制限の保存ボタン */}
          <div className="action-buttons mt-4">
            <button
              className="tsumugi-btn tsumugi-btn-primary w-full"
              onClick={async () => {
                setIsSaving(true);
                try {
                  await api.put('/api/profile', {
                    allergies,
                    allergiesOther,
                    avoidFoods,
                    avoidFoodsOther
                  });
                  setSaveSuccess(true);
                  setTimeout(() => setSaveSuccess(false), 2000);
                } catch (error) {
                  console.error('Failed to save food restrictions:', error);
                  alert('保存に失敗しました');
                } finally {
                  setIsSaving(false);
                }
              }}
              disabled={isSaving}
            >
              {isSaving ? '保存中...' : 'NG食材を保存する'}
            </button>
          </div>
        </div>

        {/* 注意書き */}
        <div className="notice-box">
          <p className="text-hint text-xs mb-2">
            ※ 設定した調味料とNG食材は「今ある食材でレシピ」「特売品でレシピ」および「栄養相談」で自動的に考慮されます
          </p>
          <p className="text-hint text-xs">
            ※ アレルギー食材は絶対に使用されません。避けたい食材も可能な限り避けられます
          </p>
        </div>

        {/* レシピに戻るボタン */}
        <div className="back-to-recipe-section">
          <button
            className="tsumugi-btn tsumugi-btn-outline w-full"
            onClick={() => navigate('/recipe-hub')}
          >
            ← レシピに戻る
          </button>
        </div>
      </div>
    </AppLayout>
  );
};

export default SeasoningsSettingsPage;
